import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Layers, Eye } from "lucide-react";
import { adminListSelectionLogs, adminSelectionLogDetail } from "@/lib/afritask";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AdminSelectionLogs() {
  const [logs, setLogs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminListSelectionLogs()
      .then((d) => setLogs(d.logs || []))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (taskId) => {
    try {
      const d = await adminSelectionLogDetail(taskId);
      setDetail(d);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-neutral-500">
          <Layers className="mr-1 inline h-3.5 w-3.5" />
          Every historical selection run — for audit + algorithm tuning.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="selection-logs-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Task</TableHead>
                <TableHead className="text-right">Entrants</TableHead>
                <TableHead className="text-right">Selected</TableHead>
                <TableHead>Bucket A / B / C</TableHead>
                <TableHead>Overflow</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-neutral-500">Loading…</TableCell></TableRow>
              )}
              {!loading && logs.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-neutral-500">No selection logs yet.</TableCell></TableRow>
              )}
              {logs.map((l) => {
                const sb = l.selected_by_bucket || {};
                return (
                  <TableRow key={l.task_id} data-testid={`sellog-row-${l.task_id}`}>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">{fmtWhen(l.selection_ran_at)}</TableCell>
                    <TableCell className="text-xs font-medium">{l.task_title}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{l.total_entrants}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{l.total_selected}</TableCell>
                    <TableCell className="text-xs">
                      <span className="rounded bg-amber-50 px-1 text-amber-800">A:{sb.A || 0}</span>{" "}
                      <span className="rounded bg-amber-50 px-1 text-amber-800">B:{sb.B || 0}</span>{" "}
                      <span className="rounded bg-emerald-50 px-1 text-emerald-700">C:{sb.C || 0}</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {l.overflow_applied ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">applied</Badge> : <span className="text-neutral-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(l.task_id)} data-testid={`open-sellog-${l.task_id}`}>
                        <Eye className="mr-1 h-3 w-3" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-[600px] max-w-full overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{detail?.task?.title || "Selection log"}</SheetTitle>
          </SheetHeader>
          {detail && (
            <ScrollArea className="mt-4 max-h-[80vh]">
              <div className="space-y-3">
                <div className="rounded-lg border p-3 text-xs">
                  <p><span className="font-semibold">Ran at:</span> {fmtWhen(detail.log.selection_ran_at)}</p>
                  <p><span className="font-semibold">Entrants:</span> {detail.log.total_entrants} · <span className="font-semibold">Selected:</span> {detail.log.total_selected}</p>
                  <p><span className="font-semibold">Buckets:</span> A:{detail.log.selected_by_bucket?.A} · B:{detail.log.selected_by_bucket?.B} · C:{detail.log.selected_by_bucket?.C}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">Per-entry breakdown</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Bucket</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead>Boosts</TableHead>
                        <TableHead>Selected</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.log.bucket_composition_details || []).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-[10px]">{e.user_id.slice(0, 8)}…</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              e.bucket === "A" ? "border-amber-200 bg-amber-50 text-amber-800" :
                              e.bucket === "B" ? "border-amber-200 bg-amber-50 text-amber-800" :
                              "border-emerald-200 bg-emerald-50 text-emerald-700"}>
                              {e.bucket}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{e.score}</TableCell>
                          <TableCell className="text-[10px] text-neutral-500">
                            {e.boosts && Object.keys(e.boosts).length > 0
                              ? Object.entries(e.boosts).map(([k, v]) => `${k}:+${v}`).join(", ")
                              : "—"}
                          </TableCell>
                          <TableCell>{e.selected ? "✅" : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
